import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";
import { toast } from "react-toastify";
import "../../styles/admin/admin-layout.css";

const ApiURL = process.env.REACT_APP_API_URL;

const NAV_ITEMS = [
  {
    path: "/admin",
    label: "Panel",
    icon: (
      <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <rect x="1" y="1" width="5" height="5" rx="1" />
        <rect x="8" y="1" width="5" height="5" rx="1" />
        <rect x="1" y="8" width="5" height="5" rx="1" />
        <rect x="8" y="8" width="5" height="5" rx="1" />
      </svg>
    ),
  },
  {
    path: "/admin/productos",
    label: "Productos",
    icon: (
      <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <rect x="1" y="1" width="12" height="12" rx="1.5" />
        <path d="M1 5h12M5 5v8" />
      </svg>
    ),
  },
  {
    path: "/admin/categorias",
    label: "Categorías",
    icon: (
      <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M2 3h10M2 7h7M2 11h5" />
      </svg>
    ),
  },
  {
    path: "/admin/marcas",
    label: "Marcas",
    icon: (
      <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="7" cy="5" r="3" />
        <path d="M2 13c0-2.2 2.2-4 5-4s5 1.8 5 4" />
      </svg>
    ),
  },
  {
    path: "/admin/stock",
    label: "Stock",
    icon: (
      <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M1 12V7l6-5 6 5v5a1 1 0 01-1 1H2a1 1 0 01-1-1z" />
        <path d="M5 13v-4h4v4" />
      </svg>
    ),
  },
  {
    path: "/admin/consultas",
    label: "Consultas",
    badge: 3, // Fallback visual
    icon: (
      <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M1 2h12a1 1 0 011 1v7a1 1 0 01-1 1H4L1 13V3a1 1 0 011-1z" />
      </svg>
    ),
  },
  {
    path: "/admin/videos",
    label: "Vídeos",
    icon: (
      <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <rect x="1" y="2" width="9" height="10" rx="1" />
        <path d="M10 5l3-2v8l-3-2V5z" />
      </svg>
    ),
  },
];

const AdminLayout = ({ children }) => {
  const { user, logout, getToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [season, setSeason] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [pendingInquiries, setPendingInquiries] = useState(0);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${ApiURL}/settings`);
        setSeason(res.data.activeSeason);
      } catch {
        toast.error("Error al cargar configuración");
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const res = await axios.get(`${ApiURL}/inquiries?status=pending&limit=1`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingInquiries(res.data.pagination.total);
      } catch (err) {
        console.error("Error cargando consultas pendientes", err);
      }
    };
    
    if (user) {
      fetchPending();
    }

    // Escuchamos el evento para actualizar el número en vivo
    window.addEventListener('updatePendingInquiries', fetchPending);

    // Limpiamos el evento al desmontar
    return () => window.removeEventListener('updatePendingInquiries', fetchPending);
  }, [user, getToken]);

  const handleToggle = async () => {
    try {
      setToggling(true);
      const token = getToken();
      const res = await axios.patch(
        `${ApiURL}/settings/toggle-season`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSeason(res.data.activeSeason);
      toast.success(`Temporada cambiada a ${res.data.activeSeason}`);
    } catch {
      toast.error("Error al cambiar temporada");
    } finally {
      setToggling(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => {
    if (path === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="al-root">
      <div
        className={`mobile-overlay ${mobileOpen ? "open" : ""}`}
        onClick={() => setMobileOpen(false)}
      />
      <nav className={`sb ${mobileOpen ? "open" : ""}`}>
        <div className="sb-brand">
          <div className="sb-brand-name">CÁCERES</div>
          <div className="sb-brand-sub">Maquinarias · Admin</div>
        </div>

        <div className="sb-nav">
          {NAV_ITEMS.map((item) => {
            const badgeValue = item.path === "/admin/consultas" && pendingInquiries > 0 ? pendingInquiries : (item.badge && pendingInquiries === 0 ? null : item.badge);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sb-item ${isActive(item.path) ? "active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >
                {item.icon}
                {item.label}
                {badgeValue ? <span className="sb-badge">{badgeValue}</span> : null}
              </Link>
            )
          })}
        </div>

        <div className="sb-footer">
          <div className="sb-email">{user?.email}</div>
          <Link
            to="/"
            className="sb-ver-web"
            onClick={() => setMobileOpen(false)}
          >
            <svg
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            >
              <circle cx="7" cy="7" r="6" />
              <path d="M7 1c-2 2-2 10 0 12M7 1c2 2 2 10 0 12M1 7h12" />
            </svg>
            Ver web
          </Link>
          <button
            className="sb-ver-web"
            onClick={handleLogout}
            style={{ marginTop: "4px", color: "#a32d2d" }}
          >
            <svg
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            >
              <path d="M5 13H2a1 1 0 01-1-1V2a1 1 0 011-1h3M10 3l3 4-3 4M13 7H4" />
            </svg>
            Salir
          </button>
        </div>
      </nav>

      <div className="main">
        <div className="topbar">
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
          >
            ☰
          </button>
          <div className="topbar-title">Panel de administración</div>
          <div className="topbar-season">
            <span className="season-dot"></span>
            <span>
              Temporada:{" "}
              <strong>
                {season
                  ? season.charAt(0).toUpperCase() + season.slice(1)
                  : "..."}
              </strong>
            </span>
          </div>
          <button
            className="season-btn"
            onClick={handleToggle}
            disabled={toggling || !season}
          >
            {toggling
              ? "Cambiando..."
              : `Cambiar a ${season === "verano" ? "invierno" : "verano"}`}
          </button>
        </div>

        <div className="content">{children}</div>
      </div>
    </div>
  );
};

export default AdminLayout;